/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { commonSuggestions } from "libs/datetime-suggestions/commonSuggestions";
import { CommonSuggestionProps } from "libs/datetime-suggestions/commonSuggestions";
import { DateTimeSuggestion } from "libs/datetime-suggestions/DateTimeSuggestion";
import { matchDateTimeSuggestions } from "libs/datetime-suggestions/matchDateTimeSuggestions";
import { UnreachableCaseError } from "libs/errors";
import { RecordValue } from "libs/schema";
import { DateTime } from "luxon";

export function getReminderSuggestions(
  input: string,
  notification: Pick<RecordValue<"notification">, "has_reminder" | "is_starred">,
) {
  const suggestions = innerGetReminderSuggestions(notification);

  return matchDateTimeSuggestions({
    input,
    suggestions,
    limit: 5,
  });
}

// Note, the order of these suggestions matters because, *all
// other things being equal*, suggestions will be displayed
// in this order here.
// In general, suggestions should be ordered from simpler to
// more complex and from most common to least common.
function innerGetReminderSuggestions(notification: Pick<RecordValue<"notification">, "has_reminder" | "is_starred">) {
  const commonSuggestionProps: CommonSuggestionProps = {
    defaultTimeForSuggestion: "SOP",
    weekStartsOn: "mo",
    weekendStartsOn: "sa",
  };

  const suggestions: Array<DateTimeSuggestion | null> = Object.values(commonSuggestions).map((factory) => {
    const suggestion = factory(commonSuggestionProps);
    setDefaultTimeForDateSuggestion(suggestion, (date) => date.set({ hour: 8 }).startOf("hour"));
    return suggestion;
  });

  // The first 5 suggestions are also shown when the user
  // hasn't typed anything. These are the "default" suggestions.
  // We splice in these suggestions related to notifications as default
  // suggestions 4 and 5.
  suggestions.splice(
    3,
    0,
    notification.is_starred ?
      new DateTimeSuggestion({
        id: "unstar",
        template: "Unstar",
        getDate: () => null,
        valueType: null,
        keywords: ["Star"],
      })
    : new DateTimeSuggestion({
        id: "star",
        template: "Star",
        getDate: () => null,
        valueType: null,
        keywords: ["Unstar"],
      }),
    (notification.has_reminder &&
      new DateTimeSuggestion({
        id: "removeReminder",
        template: "Remove reminder & move to inbox",
        getDate: () => null,
        valueType: null,
      })) ||
      null,
  );

  return suggestions;
}

function setDefaultTimeForDateSuggestion(
  suggestion: DateTimeSuggestion,
  setter: (date: DateTime<true>) => DateTime<true>,
) {
  switch (suggestion.valueType) {
    case "date":
    case "week":
    case "month":
    case "year": {
      const originalGetDate = suggestion.getDate;

      // Set the time to 8am
      suggestion.getDate = (now) => {
        const date = originalGetDate.call(suggestion, now);
        if (!date || !date.isValid) return date;
        return setter(date);
      };

      break;
    }
    case "datetime":
    case null: {
      break;
    }
    default: {
      throw new UnreachableCaseError(suggestion.valueType);
    }
  }
}
